// assets/AllCourses/Exercises/WordFormationExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function WordFormationExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student callback
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordFormationQuestions)
    ? exercise.wordFormationQuestions.map((q) => ({
        baseWord:
          typeof q.baseWord === "string" ? q.baseWord.trim() : "",
        options: Array.isArray(q.options)
          ? q.options.map((o) => String(o).trim())
          : [""],
        correctAnswers: Array.isArray(q.correctAnswers)
          ? q.correctAnswers.map((a) => String(a).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {}, // { [qIndex]: string[] }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  /* ================= RESET ON CHANGE ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordFormationQuestions", next);
  };

  const handleBaseWordChange = (qIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      baseWord: typeof value === "string" ? value.trim() : value,
    };
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];
    const options = [...q.options];
    options[optIdx] =
      typeof value === "string" ? value.trim() : value;

    next[qIdx] = {
      ...q,
      options,
      correctAnswers: q.correctAnswers.filter((a) =>
        options.includes(a)
      ),
    };

    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      options: [...next[qIdx].options, ""],
    };
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];
    const newOptions = q.options.filter((_, i) => i !== optIdx);

    next[qIdx] = {
      ...q,
      options: newOptions.length ? newOptions : [""],
      correctAnswers: q.correctAnswers.filter((a) =>
        newOptions.includes(a)
      ),
    };

    updateQuestions(next);
  };

  const handleCorrectAnswersChange = (qIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];

    const cleaned = String(value)
      .split(",")
      .map((v) => v.trim())
      .filter((v) => q.options.includes(v));

    next[qIdx] = { ...q, correctAnswers: cleaned };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        baseWord: "",
        options: [""],
        correctAnswers: [],
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleToggle = (qIdx, option) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const current = Array.isArray(prev.answers[qIdx])
        ? prev.answers[qIdx]
        : [];

      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];

      const nextAnswers = {
        ...prev.answers,
        [qIdx]: updated,
      };

      const allAnswered = questions.every(
        (_, i) =>
          Array.isArray(nextAnswers[i]) &&
          nextAnswers[i].length > 0
      );

      const allCorrect = allAnswered
        ? questions.every((q, i) => {
            const user = (nextAnswers[i] || []).map((a) =>
              normalize(a, q.requireCapitalization)
            );
            const correct = q.correctAnswers.map((a) =>
              normalize(a, q.requireCapitalization)
            );
            return (
              user.length === correct.length &&
              correct.every((c) => user.includes(c))
            );
          })
        : false;

      const next = {
        ...prev,
        answers: nextAnswers,
        hasInteracted: true,
      };

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;
        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Base Word ${qIdx + 1}`}
              value={q.baseWord}
              onChange={(v) =>
                handleBaseWordChange(qIdx, v)
              }
              placeholder="happy"
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-wf-base-${qIdx}`
                ]
              }
            />

            <div className="space-y-3">
              <label className="font-medium">Options</label>
              {q.options.map((opt, optIdx) => (
                <div
                  key={optIdx}
                  className="flex gap-3 items-end"
                >
                  <StringField
                    value={opt}
                    onChange={(v) =>
                      handleOptionChange(qIdx, optIdx, v)
                    }
                    placeholder="happiness"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      handleRemoveOption(qIdx, optIdx)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddOption(qIdx)}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answers (comma separated)"
              value={q.correctAnswers.join(", ")}
              onChange={(v) =>
                handleCorrectAnswersChange(qIdx, v)
              }
              placeholder="happiness, happily"
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-wf-correct-${qIdx}`
                ]
              }
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleCapitalizationChange(qIdx, c)
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No word formation questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Select all correct forms of the base word.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = Array.isArray(state.answers[qIdx])
            ? state.answers[qIdx]
            : [];

          const userNorm = selected.map((a) =>
            normalize(a, q.requireCapitalization)
          );
          const correctNorm = q.correctAnswers.map((a) =>
            normalize(a, q.requireCapitalization)
          );

          const allCorrect =
            userNorm.length === correctNorm.length &&
            correctNorm.every((c) => userNorm.includes(c));

          return (
            <div
              key={qIdx}
              className="p-8 bg-muted rounded-xl space-y-6 text-center"
            >
              <div className="text-4xl font-bold text-blue-700">
                {q.baseWord}
              </div>

              <div className="space-y-4 max-w-md mx-auto">
                {q.options.map((opt, optIdx) => {
                  const isSelected = selected.includes(opt);
                  const isCorrectOpt = correctNorm.includes(
                    normalize(opt, q.requireCapitalization)
                  );

                  return (
                    <div
                      key={optIdx}
                      className="flex items-center justify-center space-x-4 p-4 rounded-lg border bg-card"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() =>
                          handleToggle(qIdx, opt)
                        }
                        disabled={showResult}
                        id={`wf-${qIdx}-${optIdx}`}
                      />
                      <Label
                        htmlFor={`wf-${qIdx}-${optIdx}`}
                        className={cn(
                          "flex-1 text-xl cursor-pointer",
                          showResult &&
                            isSelected &&
                            isCorrectOpt &&
                            "text-green-700 font-bold",
                          showResult &&
                            isSelected &&
                            !isCorrectOpt &&
                            "text-red-700 font-bold"
                        )}
                      >
                        {opt}
                      </Label>
                    </div>
                  );
                })}
              </div>

              {showResult && (
                <div
                  className={cn(
                    "text-2xl font-bold",
                    allCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {allCorrect
                    ? "Perfect!"
                    : "Not quite — review the correct forms"}
                  <p className="mt-3 text-lg text-gray-700">
                    Correct:{" "}
                    <strong>
                      {q.correctAnswers.join(", ")}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
